import {
  shouldOpenNoteStructureModalOnSessionLoadAtom,
  showTranscribeOnboardingPopoverAtom,
} from '@/atoms/freeTrialModalAtoms';
import {
  openMyLibrarySubmenuAtom,
  openSourceControlPopoverAtom,
} from '@/atoms/sourceControlAtoms';
import { useChatSidebarHandlers } from '@/components/layout/Navigation/hooks/useChatSidebarHandlers';
import {
  reopenTrialExtensionModalAfterActionAtom,
  trialExtensionInviteModalOpenAtom,
  useMutateRecordGamifyDesktopAppDownloadClicked,
} from '@heidi/commerce/data';
import type { ExtensionTaskId } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useMutateCreateNewSession } from './mutation/useMutateCreateNewSession';

type TaskActionHandler = () => void;

// Deliberately not imported from `@/features/workflows/lib/preset-meta`:
// that re-export pulls in the whole `@heidi/agents/data` barrel (unrelated
// workflows/agents domain code, including jotai atoms) for one URL string.
// Matches how every other handler here hardcodes its destination inline
// (e.g. `navigateToCreateTemplate`'s `/scribe/my-templates/create-template`).
const DESKTOP_APP_DOWNLOAD_URL = 'https://www.heidihealth.com/download';

interface UseTrialExtensionTaskActionsOptions {
  onBeforeAction?: () => void;
}

export function useTrialExtensionTaskActions({
  onBeforeAction,
}: UseTrialExtensionTaskActionsOptions = {}) {
  const router = useRouter();
  const { handleNewChat } = useChatSidebarHandlers();
  const setTrialExtensionInviteModalOpen = useSetAtom(trialExtensionInviteModalOpenAtom);
  const setReopenTrialExtensionModal = useSetAtom(
    reopenTrialExtensionModalAfterActionAtom
  );
  const setShowTranscribeOnboardingPopover = useSetAtom(
    showTranscribeOnboardingPopoverAtom
  );
  const setShouldOpenNoteStructureModal = useSetAtom(
    shouldOpenNoteStructureModalOnSessionLoadAtom
  );
  const setOpenSourceControlPopover = useSetAtom(openSourceControlPopoverAtom);
  const setOpenMyLibrarySubmenu = useSetAtom(openMyLibrarySubmenuAtom);
  const { mutate: createNewSession } = useMutateCreateNewSession();
  const { mutate: recordDesktopAppDownloadClicked } =
    useMutateRecordGamifyDesktopAppDownloadClicked();

  const openInviteTeamModal = useCallback(() => {
    onBeforeAction?.();
    setTrialExtensionInviteModalOpen(true);
  }, [onBeforeAction, setTrialExtensionInviteModalOpen]);

  const navigateToCreateTemplate = useCallback(() => {
    onBeforeAction?.();
    setReopenTrialExtensionModal(true);
    void router.push('/scribe/my-templates/create-template');
  }, [onBeforeAction, router, setReopenTrialExtensionModal]);

  const openNewSessionForTemplateSelection = useCallback(() => {
    onBeforeAction?.();
    setShouldOpenNoteStructureModal(true);
    createNewSession();
  }, [onBeforeAction, setShouldOpenNoteStructureModal, createNewSession]);

  const navigateToCreateNotes = useCallback(() => {
    onBeforeAction?.();
    setShowTranscribeOnboardingPopover(true);
    createNewSession();
  }, [onBeforeAction, setShowTranscribeOnboardingPopover, createNewSession]);

  const navigateToEvidence = useCallback(() => {
    onBeforeAction?.();
    setReopenTrialExtensionModal(true);
    void router.push('/chat');
  }, [onBeforeAction, router, setReopenTrialExtensionModal]);

  // Same path as sidebar "New chat": Work Home + composer autofocus via
  // pendingWorkHomeViewAtom('new-chat'). Plain push leaves composer collapsed.
  const navigateToAskHeidi = useCallback(() => {
    onBeforeAction?.();
    setReopenTrialExtensionModal(true);
    handleNewChat();
  }, [onBeforeAction, handleNewChat, setReopenTrialExtensionModal]);

  const navigateToSourceControl = useCallback(() => {
    onBeforeAction?.();
    setReopenTrialExtensionModal(true);
    void router.push('/chat').then(() => {
      setOpenSourceControlPopover(true);
    });
  }, [onBeforeAction, router, setReopenTrialExtensionModal, setOpenSourceControlPopover]);

  const navigateToGuidelineLibrary = useCallback(() => {
    onBeforeAction?.();
    setReopenTrialExtensionModal(true);
    void router.push('/scribe/chat').then(() => {
      setOpenSourceControlPopover(true);
      requestAnimationFrame(() => setOpenMyLibrarySubmenu(true));
    });
  }, [
    onBeforeAction,
    router,
    setReopenTrialExtensionModal,
    setOpenSourceControlPopover,
    setOpenMyLibrarySubmenu,
  ]);

  const openDesktopAppDownload = useCallback(() => {
    onBeforeAction?.();
    recordDesktopAppDownloadClicked();
    window.open(DESKTOP_APP_DOWNLOAD_URL, '_blank', 'noopener,noreferrer');
  }, [onBeforeAction, recordDesktopAppDownloadClicked]);

  const taskActionHandlers = useMemo<Partial<Record<ExtensionTaskId, TaskActionHandler>>>(
    () => ({
      invite_team_member: openInviteTeamModal,
      select_note_template: openNewSessionForTemplateSelection,
      create_custom_template: navigateToCreateTemplate,
      create_notes: navigateToCreateNotes,
      submit_evidence_queries: navigateToEvidence,
      ask_heidi_tasks: navigateToAskHeidi,
      use_source_control: navigateToSourceControl,
      upload_personal_guideline: navigateToGuidelineLibrary,
      // Matches ExtensionTaskId.download_desktop_app (confirmed, GRO-4364).
      // Left as a plain string key rather than a computed
      // `[ExtensionTaskId.download_desktop_app]:` property — TS does not
      // excess-property-check this literal through useMemo's generic
      // callback, so it already compiles and matches every sibling key above.
      download_desktop_app: openDesktopAppDownload,
    }),
    [
      openInviteTeamModal,
      openNewSessionForTemplateSelection,
      navigateToCreateTemplate,
      navigateToCreateNotes,
      navigateToEvidence,
      navigateToAskHeidi,
      navigateToSourceControl,
      navigateToGuidelineLibrary,
      openDesktopAppDownload,
    ]
  );

  const getTaskHandler = useCallback(
    (taskId: ExtensionTaskId): TaskActionHandler | undefined => {
      return taskActionHandlers[taskId];
    },
    [taskActionHandlers]
  );

  return {
    getTaskHandler,
    // Exposed separately from openDesktopAppDownload (which also opens the
    // download page) so callers can silently mark this task complete without
    // navigating — used by the desktop-runtime auto-complete effect in
    // useTrialExtensionModalHandlers.
    completeDesktopAppDownloadTask: recordDesktopAppDownloadClicked,
  };
}
